/**
 * FederatedRegistry — React context provider for the orchestrator module registry.
 *
 * Wrap your app root:
 *   <FederatedRegistry scope="volvo-penta" orchestratorUrl={ORCHESTRATOR_URL}>
 *     <App />
 *   </FederatedRegistry>
 *
 * Then in any component:
 *   const module = useFederatedModule<YourModuleType>('vpc-dashboard');
 *
 * Dependencies: React + @module-federation/runtime (no other libraries required).
 *
 * Logging: All federation lifecycle events are logged with the [Federation] prefix.
 * Filter browser console by "Federation" to trace the full loading sequence.
 */
import { init } from '@module-federation/runtime';
import * as ReactQuery from '@tanstack/react-query';
// ADDED BY PC TEAM, PRESERVE
// eslint-disable-next-line no-restricted-imports 
import * as I18next from 'i18next';
import * as React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import * as JsxRuntime from 'react/jsx-runtime';
import * as ReactI18next from 'react-i18next';

import type { RegistryEntry } from './types';

// ─── Logging ─────────────────────────────────────────────────────────────────

const LOG_PREFIX = '[Federation]';

function logInfo(message: string, ...args: unknown[]) {
  console.info(`${LOG_PREFIX} ${message}`, ...args);
}

function logWarn(message: string, ...args: unknown[]) {
  console.warn(`${LOG_PREFIX} ${message}`, ...args);
}

function logError(message: string, ...args: unknown[]) {
  console.error(`${LOG_PREFIX} ${message}`, ...args);
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface RegistryContextValue {
  /** All module entries resolved from the orchestrator for the given scope. */
  entries: RegistryEntry[];
  /** True while the initial registry fetch is in progress. */
  loading: boolean;
  /** Error message if the registry fetch failed. */
  error: string | null;
  /** The orchestrator base URL. */
  orchestratorUrl: string;
}

export const RegistryContext = createContext<RegistryContextValue | null>(null);

/** @internal */
export function useRegistryContext(): RegistryContextValue {
  const ctx = useContext(RegistryContext);
  if (!ctx) throw new Error('useFederatedModule must be used inside <FederatedRegistry>');
  return ctx;
}

// ─── MF Runtime initialisation ───────────────────────────────────────────────

/**
 * Resolve the full entry URL for a registry entry.
 * Full URL: `${origin}/${name}/${version}/${bundleName}`
 */
function resolveEntry(entry: RegistryEntry): string | null {
  const version = entry.versions[0];
  if (!version?.origin || !version.bundleName || !version.version) return null;
  return `${version.origin}/${entry.name}/${version.version}/${version.bundleName}`;
}

/**
 * Pre-warm a remote's module graph with PARALLEL <link rel="modulepreload">.
 *
 * Why warm at all: the orchestrator gateway answers 503 (without CORS headers)
 * to any module request whose Referer is one of its own asset URLs — i.e. every
 * static `import` inside the remote bundle (remoteEntry.js → dist.js …). Requests
 * issued from the host document (Referer = host origin) always succeed, and the
 * later import() resolves from the document's preload cache without touching the
 * network, so the poisoned path is never taken.
 * Repro: import(remoteEntry.js) from a blank page → remoteEntry 200, its two
 * static imports fail; the same URLs via modulepreload/fetch from the document → 200.
 *
 * Why parallel: the previous version crawled the graph with serial fetch() and then
 * preloaded one file at a time with a 250 ms sleep, believing the 503s were burst
 * throttling. Measured cost: ~12–16 s before loadRemote() could start. Bursts of
 * 8–14 parallel modulepreloads from the document complete in ~200–400 ms with all
 * 200s, so the file list is taken from files.json (written by fed-publish) and
 * everything is preloaded at once. Falls back to a parallel crawl if files.json
 * is missing.
 */
async function warmModuleGraph(manifestUrl: string): Promise<void> {
  const baseUrl = manifestUrl.slice(0, manifestUrl.lastIndexOf('/') + 1);
  const start = performance.now();

  const [manifestRes, inventoryRes] = await Promise.all([fetch(manifestUrl), fetch(baseUrl + 'files.json')]);
  if (!manifestRes.ok) {
    // The orchestrator has been observed dropping a version's assets while the
    // registry still advertises it — the 404 body is JSON, so without this check
    // the MF runtime later parses it and dies with the misleading RUNTIME-013
    // "not a valid Module Federation manifest".
    throw new Error(
      `Manifest fetch failed: HTTP ${manifestRes.status} for ${manifestUrl} — ` +
      `the registry advertises a version whose assets are no longer served; the remote needs a re-publish`,
    );
  }
  const manifest = (await manifestRes.json()) as { metaData?: { remoteEntry?: { name?: string } } };
  if (!manifest?.metaData) {
    throw new Error(`Manifest at ${manifestUrl} has no metaData — not a Module Federation manifest`);
  }
  const entryFile = manifest.metaData.remoteEntry?.name ?? 'remoteEntry.js';

  let files: string[];
  if (inventoryRes.ok) {
    const inv = (await inventoryRes.json()) as { files?: string[] };
    files = (inv.files ?? []).filter((f) => f.endsWith('.js') && !f.includes('.ssr.') && !f.includes('-ssr'));
  } else {
    // Parallel crawl: discover every ./*.js reachable from the entry.
    const seen = new Set<string>();
    const crawl = async (file: string): Promise<void> => {
      if (seen.has(file)) return;
      seen.add(file);
      const res = await fetch(baseUrl + file);
      if (!res.ok) {
        seen.delete(file);
        return;
      }
      const source = await res.text();
      await Promise.all([...source.matchAll(/["'`]\.\/([\w.$-]+\.js)["'`]/g)].map((m) => crawl(m[1])));
    };
    await crawl(entryFile);
    files = [...seen];
  }
  if (!files.includes(entryFile)) files.unshift(entryFile);

  const preload = (file: string) =>
    new Promise<void>((resolve) => {
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = baseUrl + file;
      link.crossOrigin = 'anonymous';
      link.onload = () => resolve();
      link.onerror = () => {
        logWarn(`Preload failed for ${file} — continuing`);
        resolve();
      };
      document.head.appendChild(link);
      setTimeout(resolve, 10000);
    });
  await Promise.all(files.map(preload));
  logInfo(`Module graph warmed: ${files.length} file(s) in ${Math.round(performance.now() - start)}ms`);
}

let mfInitialised = false;

function ensureMfInit(entries: RegistryEntry[]): void {
  if (mfInitialised) return;
  mfInitialised = true;

  const remotes = entries
    .map((e) => {
      const entry = resolveEntry(e);
      if (!entry) {
        logWarn(`Skipping "${e.name}" — could not resolve entry URL (missing origin/version/bundleName)`);
      }
      return entry ? { name: e.name, entry } : null;
    })
    .filter((r): r is { name: string; entry: string } => r !== null);

  logInfo('Initialising @module-federation/runtime', {
    hostName: 'vpc-showcase',
    remotes: remotes.map((r) => ({ name: r.name, entry: r.entry })),
    sharedSingletons: ['react', 'react-dom', 'react/jsx-runtime', 'i18next', 'react-i18next', '@tanstack/react-query'],
  });

  init({
    name: 'vpc-showcase',
    // Match the remote's strategy: prefer loaded shares (ours) over fetching
    // version-matched fallbacks from the remote.
    shareStrategy: 'loaded-first',
    remotes,
    // Each shared entry MUST provide `lib` (the actual module) — a bare version
    // string only registers metadata, and the remote then fails with
    // "Can not get shared 'react'!" when it tries to consume the singleton.
    shared: {
      react: {
        version: React.version,
        lib: () => React,
        shareConfig: { singleton: true, requiredVersion: '^19.0.0' },
      },
      'react-dom': {
        version: React.version,
        lib: () => ReactDOM,
        shareConfig: { singleton: true, requiredVersion: '^19.0.0' },
      },
      'react/jsx-runtime': {
        version: React.version,
        lib: () => JsxRuntime,
        shareConfig: { singleton: true, requiredVersion: '^19.0.0' },
      },
      i18next: {
        // ADDED BY PC TEAM, PRESERVE
        version: __dependencyVersionsForFlopModuleFederation__['i18next'],
        lib: () => I18next,
        shareConfig: { singleton: true, requiredVersion: '^26.0.0' },
      },
      'react-i18next': {
        // ADDED BY PC TEAM, PRESERVE
        version: __dependencyVersionsForFlopModuleFederation__['react-i18next'],
        lib: () => ReactI18next,
        shareConfig: { singleton: true, requiredVersion: '^17.0.0' },
      },
      '@tanstack/react-query': {
        // ADDED BY PC TEAM, PRESERVE
        version: __dependencyVersionsForFlopModuleFederation__['@tanstack/react-query'],
        lib: () => ReactQuery,
        shareConfig: { singleton: true, requiredVersion: '^5.0.0' },
      },
    },
  });

  // Pre-seed the vite-MF module cache so the remote's share wrappers bind
  // synchronously. The remote (built with @module-federation/vite, import: false)
  // reads shares from globalThis.__mf_module_cache__.share at module evaluation
  // time; its own async bridging can finish AFTER the exposed module evaluates,
  // leaving every react binding undefined mid-render. Seeding both key forms
  // ('<name>' and 'default:<name>') makes the lookup hit immediately.
  const mfCache = ((globalThis as Record<string, unknown>).__mf_module_cache__ ??= { share: {}, remote: {} }) as {
    share: Record<string, unknown>;
    remote: Record<string, unknown>;
  };
  mfCache.share ??= {};
  const seed: Record<string, unknown> = {
    react: React,
    'react/jsx-runtime': JsxRuntime,
    'react-dom': ReactDOM,
    i18next: I18next,
    'react-i18next': ReactI18next,
    '@tanstack/react-query': ReactQuery,
  };
  for (const [name, mod] of Object.entries(seed)) {
    mfCache.share[name] ??= mod;
    mfCache.share[`default:${name}`] ??= mod;
  }
  logInfo('MF runtime initialised successfully (module cache pre-seeded)');
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface FederatedRegistryProps {
  /** The scope to query from the orchestrator, e.g. "volvo-penta". */
  scope: string;
  /**
   * Base URL of the cc-orchestrator-server.
   * If empty, the registry fetch is skipped (useFederatedModule always returns undefined).
   */
  orchestratorUrl: string;
  children: ReactNode;
}

/**
 * Fetches the federated module registry on mount, initialises @module-federation/runtime,
 * and makes the registry available to useFederatedModule() calls.
 */
export function FederatedRegistry({ scope, orchestratorUrl, children }: FederatedRegistryProps) {
  const [entries, setEntries] = useState<RegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) {
      return;
    }

    if (!orchestratorUrl) {
      logWarn('No VITE_ORCHESTRATOR_URL configured — federation disabled, using static imports only');
      setLoading(false);
      return;
    }

    fetched.current = true;

    const url = `${orchestratorUrl}/fed/dynamic-modules.json?scope=${encodeURIComponent(scope)}`;
    logInfo(`Fetching registry: ${url}`);
    const fetchStart = performance.now();

    fetch(url)
      .then((res) => {
        const elapsed = Math.round(performance.now() - fetchStart);
        if (!res.ok) {
          throw new Error(`Registry fetch failed: HTTP ${res.status} ${res.statusText} (${elapsed}ms)`);
        }
        logInfo(`Registry response: HTTP ${res.status} (${elapsed}ms)`);
        return res.json() as Promise<RegistryEntry[]>;
      })
      .then(async (data) => {
        logInfo(`Registry loaded: ${data.length} module(s) available`, data.map((e) => ({
          name: e.name,
          version: e.versions[0]?.version,
          entry: resolveEntry(e),
        })));
        // Warm every remote's module graph in parallel BEFORE consumers start
        // loadRemote(). A remote whose manifest is missing/invalid is excluded
        // entirely: registering it anyway would make loadRemote fail late with an
        // opaque RUNTIME-013, while exclusion makes consumers fall back to static
        // immediately with the real reason in the log.
        const checked = await Promise.all(
          data.map(async (e) => {
            const entry = resolveEntry(e);
            if (!entry) {
              logWarn(`Skipping "${e.name}" — could not resolve entry URL (missing origin/version/bundleName)`);
              return null;
            }
            try {
              await warmModuleGraph(entry);
              return e;
            } catch (err) {
              logError(`Remote "${e.name}" is unavailable — excluding it so consumers fall back to static.`, err);
              return null;
            }
          }),
        );
        const live = checked.filter((e): e is RegistryEntry => e !== null);
        setEntries(live);
        ensureMfInit(live);
      })
      .catch((err: Error) => {
        const elapsed = Math.round(performance.now() - fetchStart);
        logError(`Registry fetch failed after ${elapsed}ms: ${err.message}`);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [scope, orchestratorUrl]);

  return (
    <RegistryContext.Provider value={{ entries, loading, error, orchestratorUrl }}>
      {children}
    </RegistryContext.Provider>
  );
}
